/**
 * API Configuration
 * Axios instance with interceptors for authentication and offline queuing.
 *
 * Offline behaviour:
 * - Write requests (POST/PUT/DELETE) to eligible checkpoint endpoints that
 *   fail with a network error are automatically enqueued in the local
 *   IndexedDB sync queue and a synthetic success response is returned.
 * - Replayed requests (header X-Replay: true) are never re-queued.
 */

import axios from 'axios';


import { getApiBaseUrl, getApiOrigin as getOrigin } from '@/config/env';

import {
  clearTokens,
  getAccessToken,
  getCsrfToken,
  getRefreshToken,
  isCookieMode,
  refreshAccessToken,
} from './authToken';
import { enqueue } from './syncQueue';

import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// API base URL (supports runtime configuration)
const API_BASE_URL = getApiBaseUrl();

// Re-export getApiOrigin for backward compatibility
export const getApiOrigin = getOrigin;

// ─── Offline queue configuration ──────────────────────────────────────────────

/**
 * Endpoints eligible for offline queuing (relative to /api/v1).
 * Only write operations on these paths will be queued when the network is absent.
 * Patterns are matched against the request URL using startsWith or regex.
 */
const OFFLINE_ELIGIBLE_PATTERNS: RegExp[] = [
  /^\/checkpoint-passages\/?$/, // POST /checkpoint-passages/ (register arrival)
  /^\/checkpoint-passages\/\d+\/departure(\/confirm)?\/?$/, // PUT departure / confirm
  /^\/checkpoint-events\/?$/, // POST /checkpoint-events/ (create event)
  /^\/checkpoint-events\/\d+\/?$/, // PUT /checkpoint-events/:id (update event)
];

const OFFLINE_WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function isEligibleForOfflineQueuing(
  url: string | undefined,
  method: string | undefined
): boolean {
  if (!url || !method) {return false;}
  if (!OFFLINE_WRITE_METHODS.has(method.toLowerCase())) {return false;}
  // Normalise: remove trailing slash for matching, keep leading slash
  const normalised = url.replace(/\/+$/, '') || '/';
  return OFFLINE_ELIGIBLE_PATTERNS.some((pattern) => pattern.test(normalised + '/'));
}

// ─── Offline queuing logic ───────────────────────────────────────────────────

/**
 * Enqueue the request in IndexedDB and return a synthetic 202 response.
 * Called both from the request interceptor (pre-flight) and the response
 * interceptor (post-flight fallback).
 */
async function enqueueAndReturnSynthetic(
  config: InternalAxiosRequestConfig,
): Promise<AxiosResponse> {
  const operationType = deriveOperationType(config.url, config.method);
  // NOTE: we intentionally do NOT capture the Authorization header here.
  // The access token lives only 30 min, so a frozen token would be expired by
  // the time the queue is replayed. A fresh, validated token is attached at
  // replay time instead (see syncQueue.processQueue / getValidAccessToken).
  await enqueue({
    url: config.url ?? '',
    method: config.method ?? 'post',
    body: config.data
      ? (typeof config.data === 'string'
          ? JSON.parse(config.data)
          : config.data)
      : undefined,
    headers: {},
    timestamp: Date.now(),
    retryCount: 0,
    maxRetries: 5,
    status: 'pending',
    operationType,
    metadata: { url: config.url },
  });

  return {
    data: { _offlineQueued: true, success: true, message: 'offline_queued' },
    status: 202,
    statusText: 'Accepted (offline)',
    headers: {},
    config,
  } as AxiosResponse;
}

// ─── Axios instance ───────────────────────────────────────────────────────────

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes
  // Required for the HttpOnly session cookies to be sent at all. Harmless in
  // bearer mode, where there are simply no cookies to attach.
  withCredentials: true,
});

/** Methods that can change state, and therefore need the CSRF token. */
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Attach whatever proves the caller's identity for the current mode.
 *
 * Cookie mode: the browser attaches the session cookie by itself, so no
 * Authorization header is set — that is the whole point, the JWT is no longer
 * in reach of JavaScript. What we do add is the CSRF token on unsafe methods,
 * echoing the value the server handed us at login.
 *
 * Bearer mode: unchanged from before the migration.
 */
function applyAuthHeaders(config: InternalAxiosRequestConfig): void {
  if (!config.headers) {return;}

  if (isCookieMode()) {
    const csrfToken = getCsrfToken();
    if (csrfToken && UNSAFE_METHODS.has((config.method ?? '').toLowerCase())) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return;
  }

  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
}

// Request interceptor - add auth credentials + pre-flight offline queuing
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    applyAuthHeaders(config);

    // Pre-flight offline detection: if the browser is already offline and the
    // request targets an offline-eligible write endpoint, use a custom adapter
    // that skips the network call entirely and returns a synthetic response
    // after queuing the operation in IndexedDB.
    if (
      !navigator.onLine &&
      config.headers?.['X-Replay'] !== 'true' &&
      isEligibleForOfflineQueuing(config.url, config.method)
    ) {
      config.adapter = () => enqueueAndReturnSynthetic(config);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh and post-flight offline queuing
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // ── Offline queuing (post-flight fallback) ─────────────────────────────
    // Catches cases where navigator.onLine was true when the request was sent
    // but the network actually failed (e.g. lie-fi, Docker networking issue).
    const isNetworkError =
      !error.response &&
      (error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error' ||
        !navigator.onLine);
    const isReplay = originalRequest?.headers?.['X-Replay'] === 'true';

    if (
      isNetworkError &&
      !isReplay &&
      isEligibleForOfflineQueuing(originalRequest?.url, originalRequest?.method)
    ) {
      try {
        return await enqueueAndReturnSynthetic(originalRequest);
      } catch {
        // If queuing itself fails, fall through and reject as normal
      }
    }

    // ── Token refresh ────────────────────────────────────────────────────────
    // Replayed requests handle their own refresh in syncQueue.processQueue, so
    // skip the interceptor's redirect-to-login path for them.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest?.headers?.['X-Replay'] !== 'true'
    ) {
      originalRequest._retry = true;

      // Capture BEFORE refreshing: did the user actually have a session?
      // In cookie mode the refresh token is an HttpOnly cookie we cannot read,
      // so the recorded mode is what tells us a session existed.
      const hadSession = isCookieMode() || !!getRefreshToken();

      // Shared single-flight refresh (same routine the offline replay uses).
      const renewed = await refreshAccessToken();
      if (renewed) {
        // Re-apply credentials: in cookie mode the browser attaches the fresh
        // cookie itself and only the CSRF header needs refreshing; in bearer
        // mode the new access token has to replace the stale one.
        if (originalRequest.headers) {
          delete originalRequest.headers.Authorization;
        }
        applyAuthHeaders(originalRequest);
        return api(originalRequest);
      }

      // Only force a logout redirect when a REAL session just expired (renewal
      // failed on an established session). With no session — e.g. an
      // unauthenticated request fired from the /login page itself — reject
      // quietly and let the route guards handle navigation. Redirecting here
      // would reload /login → re-fire the request → 401 → reload… (infinite loop).
      if (hadSession && !window.location.pathname.startsWith('/login')) {
        // Ask the server to expire the cookies: clearing localStorage cannot
        // remove an HttpOnly cookie, and a stale one left behind would keep
        // re-authenticating the user we just decided to log out.
        try {
          await api.post('/auth/logout');
        } catch {
          // Best effort — never block the redirect on it.
        }
        clearTokens();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive a human-readable operation type string from the URL + method,
 * used for displaying the sync queue item in the UI.
 */
function deriveOperationType(url: string | undefined, method: string | undefined): string {
  if (!url) {return 'unknown';}
  const m = (method ?? '').toLowerCase();
  if (url.includes('checkpoint-passages') && m === 'post') {return 'checkpoint_arrival';}
  if (url.includes('/departure/confirm')) {return 'checkpoint_departure_confirm';}
  if (url.includes('/departure')) {return 'checkpoint_departure';}
  if (url.includes('checkpoint-events') && m === 'post') {return 'checkpoint_event_create';}
  if (url.includes('checkpoint-events') && m === 'put') {return 'checkpoint_event_update';}
  return 'unknown';
}

// Auth helpers — re-exported from the shared authToken module so existing
// imports (`from './api'`) keep working.
export {
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getCsrfToken,
  isCookieMode,
  startSession,
  downgradeToBearer,
  purgeLegacyTokens,
} from './authToken';

export default api;
